/**
 * Phase-1 closeout · LightVoucherPreview
 *
 * Thin wrapper around the canonical <VoucherPresentation> gateway,
 * scaled down for high-density surfaces (Admin → Voucher Types list,
 * dev grids). Removes the previous parallel visual interpretation that
 * approximated the voucher via a CSS-only mini card.
 *
 * Contract:
 *   • Same active `voucher.design` payload used by Guest Wallet + Builder
 *     preview drives this thumbnail — no independent design store, no
 *     independent element schema.
 *   • The composition is preserved exactly; only its rendered pixel
 *     dimensions are shrunk via CSS `transform: scale(...)`.
 *   • Rendering is memoised on the design + voucher identity so scrolling
 *     100+ voucher rows does not re-render every card on every parent
 *     paint. The thumbnail is a pure sub-tree — react's default equality
 *     via `React.memo` is sufficient.
 */
import React from 'react';
import VoucherPresentation from './VoucherPresentation';

// Full-size canonical card is ~340×476px (5/7 aspect on legacy path,
// or 340 × (340/canvas.aspect_ratio) on V2). We render at the SAME
// pixel size and use CSS `transform` to scale down to the thumbnail
// footprint — this preserves fidelity while keeping the DOM identical
// to the Guest Wallet / Builder preview.
const SOURCE_W = 340;
const SOURCE_H = 476;

function LightVoucherPreviewImpl({ voucher, width = 92, height = 128 }) {
  const scale = Math.min(width / SOURCE_W, height / SOURCE_H);
  const design = voucher?.design || null;

  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-lg"
      style={{
        width,
        height,
        // Center the scaled canonical card inside the thumbnail box.
        contain: 'strict',
      }}
      data-testid="light-voucher-preview"
      aria-hidden="true"
    >
      <div
        style={{
          width: SOURCE_W,
          height: SOURCE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
      >
        <VoucherPresentation
          voucher={{
            ...voucher,
            // Normalise legacy voucher shape → PremiumVoucherCard fields.
            name: voucher?.name || voucher?.voucher_type_name,
            code: voucher?.voucher_code || voucher?.code || '',
          }}
          design={design}
          outletName={voucher?.outlet_name || ''}
          outletInitials={(voucher?.name || 'RP')
            .split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
          state="available"
          size="phone"
        />
      </div>
    </div>
  );
}

// Memo on voucher identity — the Voucher Types table can render 100+
// rows; parent re-renders (search input keystrokes, outlet switch) must
// not thrash unchanged thumbnails.
export default React.memo(LightVoucherPreviewImpl, (prev, next) => (
  prev.width === next.width
  && prev.height === next.height
  && prev.voucher?.id === next.voucher?.id
  && prev.voucher?.updated_at === next.voucher?.updated_at
  && prev.voucher?.design === next.voucher?.design
));
